import { useCallback, useMemo } from 'react';
import { ARCHETYP_OHNE_ZUORDNUNG, isUnvalidatedAiSuggestion } from '../utils/helpers';

/**
 * Custom Hook for Filter and Sort Logic
 * Handles parameter filtering and sorting without managing state
 */
const useFilterSort = ({
  parameters,
  applyCatalogFiltersLocally = true,
  // Sortiert die API, darf der Browser nicht nachsortieren: er sieht nur die
  // aktuelle Seite und wuerde die Reihenfolge der Gesamtmenge zerstoeren
  // (argus-platform #192/#193). Lokal bleibt nur, was die API nicht kennt --
  // die Listenzugehoerigkeit.
  applySortLocally = true,
  searchTerm,
  selectedTags,
  selectedStations,
  selectedSensitivity,
  selectedOmopStatus,
  selectedSourceSystems,
  // Der Archetyp-Chip (#788): `null` heisst "kein Chip gewaehlt". Die API
  // kennt kein Filterfeld dafuer (katalogBlaetterung.js, brauchtVollsatz) --
  // anders als die uebrigen Filter oben laeuft dieser Vergleich deshalb immer,
  // unabhaengig von `applyCatalogFiltersLocally` (dieselbe Lage wie die lokale
  // Sortierung nach `openehrStatus`).
  selectedArchetype = null,
  aiSuggestedParameterIds,
  setParameterIds,
  onlyUnvalidatedAiSuggestions,
  showOnlyActiveListParams,
  activeWorkingList,
  listComparisonFilter,
  orderBy,
  order,
  parameterLists,
  isParameterInActiveList,
}) => {
  /**
   * Get parameter list status for comparison mode
   */
  const getParameterListStatus = useCallback((parameterId) => {
    if (!parameterLists || activeWorkingList.length === 0) return 'none';

    if (activeWorkingList.length === 1) {
      const list = parameterLists.find(l => l.id === activeWorkingList[0]);
      return list && list.parameterIds.includes(parameterId) ? 'inList' : 'none';
    }

    if (activeWorkingList.length === 2) {
      const list1 = parameterLists.find(l => l.id === activeWorkingList[0]);
      const list2 = parameterLists.find(l => l.id === activeWorkingList[1]);
      const inList1 = list1 && list1.parameterIds.includes(parameterId);
      const inList2 = list2 && list2.parameterIds.includes(parameterId);

      if (inList1 && inList2) return 'both';
      if (inList1) return 'list1Only';
      if (inList2) return 'list2Only';
      return 'neither';
    }

    return 'none';
  }, [parameterLists, activeWorkingList]);

  /**
   * Ob ein Parameter alle aktiven Filter trifft -- optional ohne den
   * Archetyp-Chip (#788 AK1/AK3): die Chip-Zaehlung braucht dieselbe
   * Bedingung wie die Liste, nur ohne die eine, die die Chips selbst
   * darstellen (siehe `getArchetypeCounts` weiter unten).
   */
  const matchesActiveFilters = useCallback((param, { ignoreArchetype = false } = {}) => {
    const matchesSearch =
      !applyCatalogFiltersLocally ||
      param.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      param.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTags =
      !applyCatalogFiltersLocally ||
      selectedTags.length === 0 ||
      selectedTags.some(tag => param.tags.includes(tag));

    const matchesStation =
      !applyCatalogFiltersLocally ||
      selectedStations.length === 0 ||
      selectedStations.some(station => param.stations.includes(station));

    const matchesSensitivity =
      !applyCatalogFiltersLocally ||
      !selectedSensitivity ||
      selectedSensitivity === 'all' ||
      param.sensitivity === selectedSensitivity;

    const matchesOmopStatus =
      !applyCatalogFiltersLocally ||
      !selectedOmopStatus ||
      selectedOmopStatus === 'all' ||
      param.omopStatus === selectedOmopStatus;

    const matchesSourceSystem =
      !applyCatalogFiltersLocally ||
      selectedSourceSystems.length === 0 ||
      selectedSourceSystems.includes(param.sourceSystem);

    // **Der Archetyp steht in `openehrCode`, nicht in `openehrName`** (#788).
    // Hier stand `openehrName` -- das ist seit #787 der **lokale
    // Parametername** ("% Hemoglobin A1c"), nicht der Archetypbezeichner
    // ("openEHR-EHR-CLUSTER.laboratory_test_analyte.v1"). Der Vergleich traf
    // damit nie zu, und ein Klick auf einen Chip leerte die Liste. Den Tests
    // ist es nicht aufgefallen, weil ihre Vorgaben denselben Wert in beide
    // Felder schrieben.
    //
    // Der Bucket ohne Zuordnung heisst `"keiner"` -- derselbe Sonderwert, den
    // `filter_openehr_code` serverseitig kennt, damit Chip-Beschriftung,
    // lokaler Vergleich und API-Filter dieselbe Zeichenkette benutzen und
    // nicht drei Schreibweisen fuer dieselbe Menge entstehen.
    const archetypDesParameters = param.openehrCode || ARCHETYP_OHNE_ZUORDNUNG;
    const matchesArchetype =
      ignoreArchetype ||
      selectedArchetype === null ||
      archetypDesParameters === selectedArchetype;

    // AI suggested parameters and Set parameters filter
    const hasAiFilter = aiSuggestedParameterIds && aiSuggestedParameterIds.length > 0;
    const hasSetFilter = setParameterIds && setParameterIds.length > 0;
    const matchesAiOrSetFilter =
      (!hasAiFilter && !hasSetFilter) ||
      (hasAiFilter && aiSuggestedParameterIds.includes(param.id)) ||
      (hasSetFilter && setParameterIds.includes(param.id));

    // Arbeitsliste #257 AK4: KI-Vorschlag, den noch niemand geprueft hat.
    const matchesMappingProvenance =
      !onlyUnvalidatedAiSuggestions || isUnvalidatedAiSuggestion(param);

    // Active list filter
    const matchesActiveListFilter =
      !showOnlyActiveListParams ||
      (activeWorkingList.length > 0 && isParameterInActiveList(param.id));

    // List comparison filter
    let matchesComparisonFilter = true;
    if (activeWorkingList.length === 2 && listComparisonFilter !== 'all') {
      const status = getParameterListStatus(param.id);
      matchesComparisonFilter = status === listComparisonFilter;
    }

    return (
      matchesSearch &&
      matchesTags &&
      matchesStation &&
      matchesSensitivity &&
      matchesOmopStatus &&
      matchesSourceSystem &&
      matchesArchetype &&
      matchesAiOrSetFilter &&
      matchesMappingProvenance &&
      matchesActiveListFilter &&
      matchesComparisonFilter
    );
  }, [
    applyCatalogFiltersLocally,
    searchTerm,
    selectedTags,
    selectedStations,
    selectedSensitivity,
    selectedOmopStatus,
    selectedSourceSystems,
    selectedArchetype,
    aiSuggestedParameterIds,
    setParameterIds,
    onlyUnvalidatedAiSuggestions,
    showOnlyActiveListParams,
    activeWorkingList,
    listComparisonFilter,
    isParameterInActiveList,
    getParameterListStatus,
  ]);

  /**
   * Filter parameters based on all active filters
   */
  const filteredParameters = useMemo(() => {
    return parameters.filter(param => matchesActiveFilters(param));
  }, [parameters, matchesActiveFilters]);

  /**
   * Zaehlung je Archetyp unter allen aktiven Filtern **ausser** dem
   * Archetyp-Chip selbst (#788 AK1/AK3, Review Runde 3 Blocker 3): ein Chip
   * zaehlt "wie viele Zeilen traefe ich, waehlte ich diesen Archetyp", nicht
   * "wie viele Zeilen bleiben, wenn zusaetzlich noch der aktuell gewaehlte
   * Archetyp gilt" -- sonst wuerde die Summe aller Chips nie die Gesamtzahl
   * der Liste treffen (AK1), sobald ueberhaupt ein Chip aktiv ist. Die
   * uebrigen Filter -- Suche, Tags, Stationen, Status **und** die fuenf
   * listenbasierten Filter aus useFilterSort (Arbeitsliste, Listenvergleich,
   * KI-Vorschlaege, Set, ungeprueft) -- gelten unveraendert, ueber dieselbe
   * `matchesActiveFilters` wie `filteredParameters`.
   *
   * Nur dort aussagekraeftig, wo `parameters` bereits die vollstaendige
   * gefilterte Menge ist (Vollsatz- oder ids-Filter-Modus, siehe
   * `blaettertLokal` in DataCatalog.js) -- bei einer einzelnen Seite waere
   * das Ergebnis die Zaehlung ueber die sichtbaren 50, nicht ueber den
   * Bestand; DataCatalog.js ruft diese Funktion deshalb nur dort auf.
   */
  const getArchetypeCounts = useCallback(() => {
    const zaehlung = new Map();
    parameters.forEach(param => {
      if (!matchesActiveFilters(param, { ignoreArchetype: true })) return;
      // Derselbe Schluessel wie beim Filtern oben (#788): der Archetyp aus
      // `openehrCode`, nicht der lokale Name -- sonst zaehlt die Chip-Zeile
      // andere Gruppen, als der Klick darauf filtert.
      const key = param.openehrCode || ARCHETYP_OHNE_ZUORDNUNG;
      zaehlung.set(key, (zaehlung.get(key) || 0) + 1);
    });
    return zaehlung;
  }, [parameters, matchesActiveFilters]);

  /**
   * Get sort value for a parameter
   */
  const getSortValue = useCallback((param) => {
    if (orderBy === 'lists') {
      const status = getParameterListStatus(param.id);
      return status === 'inList' ? 1 : 0;
    }
    if (orderBy === 'listA') {
      const status = getParameterListStatus(param.id);
      return status === 'list1Only' || status === 'both' ? 1 : 0;
    }
    if (orderBy === 'listB') {
      const status = getParameterListStatus(param.id);
      return status === 'list2Only' || status === 'both' ? 1 : 0;
    }
    // Die openEHR-Zuordnungsspalte (#772, Review Runde 2 Blocker B1): die API
    // kennt kein Sortierfeld dafuer (siehe LOKALE_SORTIERFELDER in
    // katalogBlaetterung.js), sortiert wird deshalb hier -- am Rohwert, nicht
    // an einer Rangfolge, weil die Kopfzeile nur "Klick sortiert etwas
    // Sinnvolles", keine bestimmte Reihenfolge der drei Zustaende verspricht.
    if (orderBy === 'openehrStatus') {
      return param.openehrStatus || '';
    }
    return 0;
  }, [orderBy, getParameterListStatus]);

  /**
   * Sort filtered parameters
   */
  const sortedParameters = useMemo(() => {
    if (!orderBy || !applySortLocally) return filteredParameters;

    return [...filteredParameters].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      // Die Listenspalten liefern Zahlen (0/1), die openEHR-Statusspalte eine
      // Zeichenkette -- `-` waere dort `NaN` und liesse jede Reihenfolge
      // stehen, wie sie war.
      const vergleich = typeof aValue === 'string' || typeof bValue === 'string'
        ? String(aValue).localeCompare(String(bValue))
        : aValue - bValue;

      return order === 'asc' ? vergleich : -vergleich;
    });
  }, [filteredParameters, orderBy, order, applySortLocally, getSortValue]);

  /**
   * Get list comparison counts for UI display.
   *
   * Drei Faelle fuer `totalFilteredCount` (#277 Runde 2 B4, Runde 3 NEU-B6):
   *
   * - `undefined` (Vollsatz-Modus, `listComparisonFilter` ist `'all'` oder
   *   `'neither'`, siehe `brauchtVollsatz`): `parameters` ist die volle
   *   gefilterte Menge, `neither` zaehlt direkt daraus.
   * - eine Zahl (ids-Filter-Modus, `list1Only`/`list2Only`/`both`
   *   ausgewaehlt): `parameters` enthaelt nur die Vereinigung beider Listen,
   *   nie ein Element mit Status `neither` -- `neither` folgt stattdessen aus
   *   `totalFilteredCount - (list1Only + list2Only + both)`.
   * - `null` (ids-Filter-Modus, aber der separate Gesamtzahl-Abruf ist noch
   *   nicht da oder fehlgeschlagen): `neither` ist **unbekannt**, nicht 0 --
   *   direkt aus `parameters` zaehlen waere hier immer 0, weil die Vereinigung
   *   nie ein `neither`-Element enthaelt (NEU-B6: der Chip zeigte "Keine (0)"
   *   statt "unbekannt", wenn der Zusatzabruf ausfiel).
   */
  const getListComparisonCounts = (totalFilteredCount) => {
    if (!parameterLists || activeWorkingList.length !== 2) return null;

    const list1 = parameterLists.find(l => l.id === activeWorkingList[0]);
    const list2 = parameterLists.find(l => l.id === activeWorkingList[1]);

    if (!list1 || !list2) return null;

    const counts = {
      list1Only: 0,
      list2Only: 0,
      both: 0,
      neither: 0,
    };

    parameters.forEach(param => {
      const status = getParameterListStatus(param.id);
      if (status === 'list1Only') counts.list1Only++;
      else if (status === 'list2Only') counts.list2Only++;
      else if (status === 'both') counts.both++;
      else if (totalFilteredCount === undefined) counts.neither++;
    });

    if (totalFilteredCount === null) {
      counts.neither = null;
    } else if (totalFilteredCount !== undefined) {
      counts.neither = Math.max(0, totalFilteredCount - (counts.list1Only + counts.list2Only + counts.both));
    }

    return counts;
  };

  return {
    filteredParameters,
    sortedParameters,
    getParameterListStatus,
    getListComparisonCounts,
    getArchetypeCounts,
  };
};

export default useFilterSort;
